import { useState } from 'react';

import ReferenceDocumentModal from '../ReferenceDocumentModal/ReferenceDocumentModal';

import styles from './ReferenceDocumentListSection.module.scss';

/**
 * ReferenceDocumentListSection 컴포넌트
 * - 문서 작성 페이지 전용 참조문서 섹션 컴포넌트
 * - 피그마 디자인 기반 (node-id=1241-9605)
 * - 여러 문서를 리스트로 한 번에 표시하는 형태
 * - 테이블 형식: 상신자, 제목 컬럼
 *
 * @param {Array} referenceDocuments - 참조문서 목록
 * @param {Function} onReferenceDocumentsChange - 참조문서 변경 핸들러
 * @param {boolean} isPreview - 미리보기 모드 여부
 * @param {Function} onDocumentRowClick - 참조문서 선택 모달 내 문서 목록 행 클릭 시 또는 추가된 참조문서 행 클릭 시 호출 (문서상세보기 열기용)
 */
function ReferenceDocumentListSection({
  referenceDocuments = [],
  onReferenceDocumentsChange,
  isPreview = false,
  onDocumentRowClick,
}) {
  const [modalOpen, setModalOpen] = useState(false);

  // 참조문서 선택 핸들러
  function handleReferenceSelect(selectedList) {
    if (onReferenceDocumentsChange) {
      onReferenceDocumentsChange(selectedList);
    }
    setModalOpen(false);
  }

  return (
    <>
      <div className={styles.referenceSection}>
        {/* 섹션 헤더 - 타이틀 + 선택 버튼 */}
        <div className={styles.sectionHeaderContainer}>
          <h3 className={styles.sectionTitle}>참조문서</h3>
          {!isPreview && (
            <button
              type="button"
              className={styles.selectButton}
              onClick={function () {
                setModalOpen(true);
              }}
            >
              선택
            </button>
          )}
        </div>

        {/* 테이블 컨테이너 */}
        <div className={styles.tableWrapper}>
          {/* 상단 파란색 라인 */}
          <div className={styles.tableTopLine} />

          {/* 테이블 헤더 (고정) */}
          <div className={styles.tableHeader}>
            <div className={`${styles.headerCell} ${styles.headerCellSender}`}>상신자</div>
            <div className={`${styles.headerCell} ${styles.headerCellTitle}`}>제목</div>
          </div>

          {/* 문서 목록 (스크롤 가능) */}
          <div className={styles.tableBody}>
            {referenceDocuments.length === 0 ? (
              <div className={styles.documentRow}>
                <div className={`${styles.documentField} ${styles.documentFieldSender}`}>
                  <span className={styles.value}>-</span>
                </div>
                <div className={`${styles.documentField} ${styles.documentFieldTitle}`}>
                  <span className={styles.value}>-</span>
                </div>
              </div>
            ) : (
              referenceDocuments.map(function (doc, index) {
                const senderName = doc?.sender || doc?.drafter || doc?.name || '-';
                const docTitle = doc?.title || '-';
                const isClickable = !isPreview && onDocumentRowClick && doc?.idx != null;

                function handleRowClick() {
                  if (isClickable && onDocumentRowClick) {
                    onDocumentRowClick(doc);
                  }
                }

                function handleRowKeyDown(e) {
                  if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    handleRowClick();
                  }
                }

                return (
                  <div
                    key={doc?.idx || index}
                    className={`${styles.documentRow} ${isClickable ? styles.clickableRow : ''}`}
                    role={isClickable ? 'button' : undefined}
                    tabIndex={isClickable ? 0 : undefined}
                    onClick={isClickable ? handleRowClick : undefined}
                    onKeyDown={isClickable ? handleRowKeyDown : undefined}
                  >
                    <div className={`${styles.documentField} ${styles.documentFieldSender}`}>
                      <span className={styles.value}>{senderName}</span>
                    </div>
                    <div className={`${styles.documentField} ${styles.documentFieldTitle}`}>
                      <span className={styles.value}>{docTitle}</span>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* 하단 구분선 */}
          <div className={styles.tableBottomLine} />
        </div>
      </div>

      {!isPreview && (
        <ReferenceDocumentModal
          isOpen={modalOpen}
          onClose={function () {
            setModalOpen(false);
          }}
          onSelect={handleReferenceSelect}
          selectedDocuments={referenceDocuments}
          onDocumentRowClick={onDocumentRowClick}
        />
      )}
    </>
  );
}

export default ReferenceDocumentListSection;
